import React, { memo, useRef } from 'react';
import useScreenSize from '../../../hooks/useScreenSize';
import { BottomPhoneButtonProps } from './types';
import {
  PhoneButtonContainer,
  popoverHorizontalAlign,
  popoverStyles
} from './styled';
import { PhoneButtonTargetTypeEnum } from '../../../hooks/useFullPhoneNumbers';
import { BottomPhoneContactsModal } from './BottomPhoneContactsModal';
import { PhoneButton } from './PhoneButton';
import { Popover } from '../../ui/Popover';
import { PhoneLinksList } from './PhoneLinksList';
import { DesktopPhoneButton } from './DesktopPhoneButton';

export const BottomPhoneButton: React.FC<BottomPhoneButtonProps> = memo(
  ({
    stat,
    phones = [],
    clickHandler,
    isIconButton = false,
    isAuthor = false,
    isGallery = false,
    setIsVisible,
    isVisible,
    contactInfoModalTitle,
    targetTriggerButton
  }) => {
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const { isSmallOrLargeDesktop } = useScreenSize();

    const onClickHandler = (e: React.SyntheticEvent<HTMLElement>) => {
      e.stopPropagation();

      if (!isAuthor) {
        const phoneButtonTargetType = isGallery
          ? PhoneButtonTargetTypeEnum.gallery
          : PhoneButtonTargetTypeEnum.main;

        clickHandler(0, false)(e, targetTriggerButton || phoneButtonTargetType);
      }
    };

    const isPhonesOpened = !!phones?.length;
    const isOnePhone = phones?.length === 1;
    const firstPhone = phones?.[0];
    const isManyPhones = (phones?.length || 0) > 1;
    const isPhonesShown =
      isPhonesOpened &&
      isSmallOrLargeDesktop &&
      (!isGallery || (isGallery && isOnePhone));
    const showDesktopButton = isGallery || isSmallOrLargeDesktop;

    return (
      <>
        {!isGallery && (
          <PhoneButton
            stat={stat}
            isIconButton={isIconButton}
            phones={phones}
            onClickHandler={onClickHandler}
          />
        )}

        {showDesktopButton && (
          <PhoneButtonContainer
            onClick={onClickHandler}
            $isGallery={isGallery}
            $isPhoneShown={isPhonesShown}
            ref={triggerRef}
            data-stat={stat}
          >
            {isGallery && !isSmallOrLargeDesktop && (
              <DesktopPhoneButton isGallery />
            )}

            {!isGallery && isSmallOrLargeDesktop && (
              <>
                {!phones?.length && <DesktopPhoneButton />}
                {!!phones?.length && <PhoneLinksList phones={phones} />}
              </>
            )}

            {isGallery && isSmallOrLargeDesktop && (
              <>
                {(!isPhonesOpened || (isPhonesOpened && isManyPhones)) && (
                  <DesktopPhoneButton isGallery />
                )}

                {isPhonesOpened && isOnePhone && firstPhone && (
                  <PhoneLinksList phones={[firstPhone]} isGallery />
                )}

                {isPhonesOpened && isManyPhones && isVisible && (
                  <Popover
                    triggerRef={triggerRef}
                    isOpen={isVisible}
                    closeHandler={() => setIsVisible?.(false)}
                    horizontalAlign={popoverHorizontalAlign}
                    styles={popoverStyles}
                    cypress="gallery-phones-list"
                  >
                    <PhoneLinksList phones={phones} showIcon />
                  </Popover>
                )}
              </>
            )}
          </PhoneButtonContainer>
        )}

        {isManyPhones && isVisible && !isSmallOrLargeDesktop && (
          <BottomPhoneContactsModal
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            contactInfoModalTitle={contactInfoModalTitle}
            phones={phones}
          />
        )}
      </>
    );
  }
);

BottomPhoneButton.displayName = 'BottomPhoneButton';
